import { ui } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import type { TemplateT } from "@/types/database/quickstarts";
import { Link } from "@tanstack/react-router";
import { QrCode, Send } from "lucide-react";
import React, { Suspense, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import type { TemplateSettingsSchemaT } from "../../../types";
import SignupTitle from "../../auth/components/SignupTitle";
import EventsPreview from "./EventsPreview";
import TemplateControls from "./TemplateControls";
import TemplateQRCode from "./TemplateQRCode";
import TrackersPreview from "./TrackersPreview";

const quickstartId = uuidv4();

function TemplateCard({
  template,
}: {
  template: TemplateT & { templates_access: { access: "viewer" | "editor" }[] };
}) {
  const [showQR, setShowQR] = useState(false);
  return (
    <ui.Card className="relative flex flex-col bg-white dark:bg-slate-900 bg-gradient-to-br hover:bg-gradient-to-br from-indigo-50/20 hover:from-white via-slate-50/50 hover:via-slate-50/50 to-indigo-50/40 hover:to-indigo-100/30 shadow-sm hover:shadow-lg border border-slate-100 hover:border-slate-200 rounded-lg h-[480px] max-h-[480px] overflow-hidden text-slate-950 dark:text-slate-50 transition-all duration-300">
      <ui.CardHeader className="flex flex-col p-0 min-h-0">
        <div className="flex justify-between items-center bg-gradient-to-r from-indigo-500/10 to-purple-500/10 p-4">
          <TemplateControls template={template} />
        </div>
      </ui.CardHeader>
      <ui.CardContent className="flex flex-col flex-1 gap-2 p-4 h-full min-h-0">
        <Suspense fallback={<Skeleton className="mb-2 w-full h-8" />}>
          <TrackersPreview template={template} />
        </Suspense>
        <Suspense fallback={<Skeleton className="w-full h-24" />}>
          <EventsPreview
            settings={template.settings as TemplateSettingsSchemaT}
          />
        </Suspense>
      </ui.CardContent>
      <ui.CardFooter className="right-0 bottom-0 left-0 absolute bg-white/95 backdrop-blur-sm border-slate-100 border-t">
        <div className="flex gap-2 w-full">
          <ui.Button asChild size="sm" className="flex-no-wrap gap-2 w-full">
            <Link
              to="/new/diary/invite/diary"
              search={{
                ...template.settings,
                quickstart_key: template?.quickstart_key ?? "PAIN",
                quickstart_id: quickstartId,
              }}
            >
              Send Invite
              <Send className="w-5 h-5 transition-transform group-hover:translate-x-[1px] group-hover:translate-y-[-1px]" />
            </Link>
          </ui.Button>
          <ui.Dialog open={showQR} onOpenChange={setShowQR}>
            <ui.DialogTrigger asChild>
              <ui.Button
                size="sm"
                variant="secondary"
                className="flex-no-wrap gap-2 w-full"
              >
                Show QR
                <QrCode className="w-5 h-5 group-hover:rotate-6 transition-transform" />
              </ui.Button>
            </ui.DialogTrigger>
            <ui.DialogContent className="bg-indigo-100 border border-indigo-300">
              <div className="flex flex-col justify-center items-center min-h-[120px]">
                <SignupTitle search={template.settings} />
                {showQR
                  ? (
                    <Suspense
                      fallback={<Skeleton className="w-[250px] h-[250px]" />}
                    >
                      <TemplateQRCode template={template} />
                    </Suspense>
                  )
                  : null}
              </div>
              <ui.DialogFooter>
                <ui.DialogClose asChild>
                  <Button variant="outline">
                    Close
                  </Button>
                </ui.DialogClose>
              </ui.DialogFooter>
            </ui.DialogContent>
          </ui.Dialog>
        </div>
      </ui.CardFooter>
    </ui.Card>
  );
}

export default TemplateCard;
